import React, { useState, useEffect, useRef, memo, useCallback } from 'react';
import { Paper, Title, Stack, Text, Group, Progress, Badge, Accordion, Box, Select, useMantineTheme, Timeline, Button, ActionIcon, Tooltip } from '@mantine/core';
import { IconCircleDot, IconPlayerPlay, IconCheck, IconLock, IconListCheck, IconSchool } from '@tabler/icons-react';
import { CourseContent, CourseState, TodoItem, TodoStatus } from '../types';
import { courseService } from '../services/courseService';
import { notifications } from '@mantine/notifications';
import TextArea from '../../../components/TextArea';

interface TodoListProps {
  moduleContent: CourseContent;
  courseState: CourseState;
  courseId: string;
}

// Simplified TodoItem without deadlines and priorities
interface EnhancedTodoItem extends TodoItem {
  completedDate?: Date;
}

const TodoList: React.FC<TodoListProps> = ({ moduleContent, courseState, courseId }) => {
  const [todos, setTodos] = useState<EnhancedTodoItem[]>([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const theme = useMantineTheme();

  // Type guard to check if content has todos
  const hasTodos = (content: CourseContent): content is CourseContent & { todos: EnhancedTodoItem[] } => {
    return Array.isArray((content as any).todos);
  };

  // Load todos on mount and when moduleContent changes
  useEffect(() => {
    const loadTodos = async () => {
      if (!moduleContent || !courseId) return;

      try {
        setLoading(true);
        // Check if todos exist for this module
        if (!hasTodos(moduleContent)) {
          // Generate and save todos
          const generatedTodos = generateTodoItems(moduleContent);
          await courseService.initializeTodos(courseId, generatedTodos);
          setTodos(generatedTodos);
        } else {
          setTodos(moduleContent.todos);
        }
      } catch (error) {
        console.error('Error loading todos:', error);
        setError('Failed to load todos');
      } finally {
        setLoading(false);
      }
    };

    loadTodos();
  }, [moduleContent, courseId]);

  // Generate todo items from module content
  const generateTodoItems = (moduleContent: CourseContent): EnhancedTodoItem[] => {
    // If moduleContent is undefined or lacks required properties, return an empty array
    if (!moduleContent || 
        (!moduleContent.realWorldActions && 
         !moduleContent.assignment)) {
      console.warn('No content available for generating todos');
      return [];
    }

    const todos: EnhancedTodoItem[] = [];

    // Add real world actions
    (moduleContent.realWorldActions || []).forEach((action, index) => {
      todos.push({
        id: `action-${index}`,
        text: action,
        status: 'not_started',
        category: 'action',
        notes: ''
      });
    });

    // Add assignment
    if (moduleContent.assignment) {
      todos.push({
        id: 'assignment-1',
        text: moduleContent.assignment,
        status: 'not_started',
        category: 'assignment',
        notes: ''
      });
    }

    return todos;
  };

  // Update todo status
  const updateTodoStatus = async (todoId: string, newStatus: TodoStatus, notes: string) => {
    const updatedTodos = todos.map((todo) => {
      if (todo.id === todoId) {
        const updatedTodo = { 
          ...todo, 
          status: newStatus, 
          notes 
        };
        
        // Add completion date if marked as completed
        if (newStatus === 'completed' && !todo.completedDate) {
          updatedTodo.completedDate = new Date();
        } else if (newStatus !== 'completed') {
          // Remove completion date if unmarked
          delete updatedTodo.completedDate;
        }
        
        return updatedTodo;
      }
      return todo;
    });

    setTodos(updatedTodos);

    if (courseId) {
      try {
        await courseService.updateTodos(courseId, updatedTodos);
        
        // Show notification for completed items
        const completedTodo = updatedTodos.find(t => t.id === todoId && t.status === 'completed');
        if (completedTodo) {
          notifications.show({
            title: '🎉 Task Completed!',
            message: `Great job completing: ${completedTodo.text.substring(0, 30)}...`,
            color: 'green'
          });
        }
      } catch (error) {
        console.error('Failed to update todo status:', error);
      }
    }
  };

  const getNextStatus = (currentStatus: TodoStatus): TodoStatus => {
    switch (currentStatus) {
      case 'not_started':
        return 'in_progress';
      case 'in_progress':
        return 'completed';
      case 'completed':
        return 'not_started';
    }
  };

  const calculateProgress = () => {
    if (todos.length === 0) return 0;
    const completedCount = todos.filter(todo => todo.status === 'completed').length;
    const inProgressCount = todos.filter(todo => todo.status === 'in_progress').length;
    return Math.round(((completedCount + inProgressCount * 0.5) / todos.length) * 100);
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'action':
        return 'green';
      case 'assignment':
        return 'violet';
      default:
        return 'gray';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'action':
        return <IconListCheck size={18} />;
      case 'assignment':
        return <IconSchool size={18} />;
      default:
        return <IconCircleDot size={18} />;
    }
  };

  const getStatusColor = (status: TodoStatus) => {
    switch (status) {
      case 'completed':
        return 'green';
      case 'in_progress':
        return 'blue';
      case 'not_started':
        return 'gray';
    }
  };

  const getStatusIcon = (status: TodoStatus) => {
    switch (status) {
      case 'completed':
        return <IconCheck size={18} />;
      case 'in_progress':
        return <IconPlayerPlay size={18} />;
      case 'not_started':
        return <IconCircleDot size={18} />;
    }
  };

  const getStatusLabel = (status: TodoStatus): string => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'in_progress':
        return 'In Progress';
      case 'not_started':
        return 'Not Started';
    }
  };

  const progress = calculateProgress();

  // Todo Notes component
  const TodoNotes = memo(({ 
    todoId, 
    initialNotes, 
    isDisabled, 
    onUpdate 
  }: { 
    todoId: string;
    initialNotes: string;
    isDisabled: boolean;
    onUpdate: (todoId: string, notes: string) => void;
  }) => {
    const [localNotes, setLocalNotes] = useState(initialNotes);
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
      setLocalNotes(initialNotes);
    }, [initialNotes]);

    const handleNotesChange = (notes: string) => {
      if (isDisabled) return;
      setLocalNotes(notes);
    };

    const handleBlur = () => {
      setIsFocused(false);
      if (!isDisabled && localNotes !== initialNotes) {
        onUpdate(todoId, localNotes);
      }
    };

    return (
      <TextArea
        placeholder={isDisabled ? "Notes disabled" : "Add your notes here..."}
        value={localNotes}
        onChange={handleNotesChange}
        disabled={isDisabled}
        minRows={2}
        maxRows={4}
        autosize
        onFocus={() => setIsFocused(true)}
        onBlur={handleBlur}
        styles={(theme) => ({
          root: {
            transition: 'all 0.2s ease',
            position: 'relative',
            '&::after': {
              content: isDisabled ? '"🔒"' : '"✏️"',
              position: 'absolute',
              top: '8px',
              right: '8px',
              opacity: 0.5,
              pointerEvents: 'none',
            }
          },
          input: {
            backgroundColor: isDisabled 
              ? theme.colors.gray[1]
              : isFocused 
                ? theme.white
                : theme.fn.rgba(theme.colors.gray[0], 0.7),
            border: `${isDisabled ? '1px' : '2px'} solid ${
              isDisabled 
                ? theme.colors.gray[4]
                : isFocused
                  ? theme.colors.blue[5]
                  : theme.colors.gray[3]
            }`,
          }
        })}
      />
    );
  });

  // TodoStatusToggle component
  const TodoStatusToggle = memo(({ 
    todo, 
    onUpdate 
  }: { 
    todo: EnhancedTodoItem; 
    onUpdate: () => void;
  }) => {
    const [localStatus, setLocalStatus] = useState(todo.status);
    const [isAnimating, setIsAnimating] = useState(false);
    const [localNotes, setLocalNotes] = useState(todo.notes);
    const isDisabled = courseState !== 'in_progress';

    const theme = useMantineTheme();

    // Update when the todo prop changes
    useEffect(() => {
      if (todo.status !== localStatus) {
        setLocalStatus(todo.status);
      }
      if (todo.notes !== localNotes) {
        setLocalNotes(todo.notes);
      }
    }, [todo]);

    const handleStatusUpdate = async () => {
      if (isDisabled) {
        notifications.show({
          title: 'Course is not in progress',
          message: 'You can only update todos when the course is in progress.',
          color: 'yellow'
        });
        return;
      }

      setIsAnimating(true);
      const newStatus = getNextStatus(localStatus);
      setLocalStatus(newStatus);
      
      setTimeout(() => {
        setIsAnimating(false);
        updateTodoStatus(todo.id, newStatus, localNotes);
        onUpdate();
      }, 300);
    };

    const handleNotesUpdate = (todoId: string, notes: string) => {
      if (isDisabled) return;
      
      setLocalNotes(notes);
      updateTodoStatus(todo.id, localStatus, notes);
    };

    return (
      <Stack spacing="xs">
        <Group position="apart" align="center" spacing="xs">
          <Group spacing="sm">
            <ActionIcon 
              variant="light"
              color={getStatusColor(localStatus)}
              size="lg"
              radius="xl"
              onClick={handleStatusUpdate}
              className={isAnimating ? 'rotate-animation' : ''}
              disabled={isDisabled}
              sx={{
                transition: 'transform 0.3s ease',
                '&.rotate-animation': {
                  transform: 'rotate(180deg)',
                },
              }}
            >
              {getStatusIcon(localStatus)}
            </ActionIcon>
            
            <Stack spacing={0}>
              <Text weight={500} size="sm">
                {todo.text}
              </Text>
              <Group spacing="xs">
                <Badge size="xs" color={getStatusColor(localStatus)}>
                  {getStatusLabel(localStatus)}
                </Badge>
                <Badge size="xs" color={getCategoryColor(todo.category)}>
                  {todo.category === 'action' ? 'Action' : 'Project'}
                </Badge>
              </Group>
            </Stack>
          </Group>
        </Group>
        
        <Box mt="xs">
          <TodoNotes 
            todoId={todo.id} 
            initialNotes={localNotes} 
            isDisabled={isDisabled}
            onUpdate={handleNotesUpdate}
          />
        </Box>
        
        {localStatus === 'completed' && todo.completedDate && (
          <Text size="xs" color="dimmed" ta="right">
            Completed on {todo.completedDate.toLocaleDateString()}
          </Text>
        )}
      </Stack>
    );
  });

  const renderCategoryProgress = (category: string, items: EnhancedTodoItem[]) => {
    if (items.length === 0) return null;
    
    const completedCount = items.filter(todo => todo.status === 'completed').length;
    const inProgressCount = items.filter(todo => todo.status === 'in_progress').length;
    const progress = Math.round(((completedCount + inProgressCount * 0.5) / items.length) * 100);
    
    return (
      <Group position="apart" align="center" noWrap>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {getCategoryIcon(category)}
          <Text weight={500} size="sm">
            {category === 'action' ? 'Practical Actions' : 'Final Project'}
          </Text>
        </Box>
        <Group spacing="xs" noWrap>
          <Progress
            value={progress}
            color={getCategoryColor(category)}
            size="lg"
            radius="xl"
            style={{ width: 100 }}
          />
          <Text size="sm" weight={500} color={getCategoryColor(category)}>
            {progress}%
          </Text>
        </Group>
      </Group>
    );
  };

  type CategoryConfig = {
    value: string;
    title: string;
    filter: (todo: TodoItem) => boolean;
  };

  // Define the categories we want to show
  const categories: CategoryConfig[] = [
    {
      value: 'actions',
      title: 'Practical Actions',
      filter: (todo) => todo.category === 'action',
    },
    {
      value: 'project',
      title: 'Final Project',
      filter: (todo) => todo.category === 'assignment',
    }
  ];

  const AccordionSection: React.FC<{
    category: CategoryConfig;
    todos: EnhancedTodoItem[];
  }> = ({ category, todos }) => {
    const [refreshKey, setRefreshKey] = useState(0);
    const filteredTodos = todos.filter(category.filter);
    
    // Skip rendering if there are no todos for this category
    if (filteredTodos.length === 0) return null;

    const handleTodoUpdated = () => {
      setRefreshKey(prev => prev + 1);
    };

    return (
      <Box key={refreshKey}>
        {renderCategoryProgress(category.value === 'actions' ? 'action' : 'assignment', filteredTodos)}
        <Stack spacing="md" mt="md">
          {filteredTodos.map((todo) => (
            <Paper
              key={todo.id}
              p="md"
              withBorder
              sx={(theme) => ({
                borderLeft: `4px solid ${
                  todo.status === 'completed' 
                    ? theme.colors.green[5] 
                    : todo.status === 'in_progress'
                      ? theme.colors.blue[5]
                      : theme.colors.gray[5]
                }`,
                backgroundColor: todo.status === 'completed' 
                  ? theme.fn.rgba(theme.colors.green[1], 0.3)
                  : todo.status === 'in_progress'
                    ? theme.fn.rgba(theme.colors.blue[1], 0.2)
                    : theme.white,
                transition: 'all 0.2s ease',
              })}
            >
              <TodoStatusToggle todo={todo} onUpdate={handleTodoUpdated} />
            </Paper>
          ))}
        </Stack>
      </Box>
    );
  };

  return (
    <Paper withBorder p="md" radius="md">
      <Stack spacing="md">
        <Group position="apart">
          <Title order={4}>Learning Progress</Title>
          <Badge size="lg" color={progress === 100 ? 'green' : 'blue'}>
            {progress}% Complete
          </Badge>
        </Group>

        <Progress
          size="xl"
          radius="xl"
          value={progress}
          color={progress === 100 ? 'green' : 'blue'}
          animate
        />

        <Accordion defaultValue={categories[0]?.value} styles={{
          item: {
            borderBottom: `1px solid ${theme.colors.gray[3]}`,
            '&:last-of-type': {
              borderBottom: 'none',
            }
          },
          control: {
            padding: `${theme.spacing.sm}px 0`,
          },
          content: {
            padding: `${theme.spacing.xs}px 0 ${theme.spacing.md}px`,
          }
        }}>
          {categories.map((category) => (
            <Accordion.Item key={category.value} value={category.value}>
              <Accordion.Control>
                <Group spacing="xs">
                  {category.value === 'actions' 
                    ? <IconListCheck size={20} /> 
                    : <IconSchool size={20} />}
                  <Text weight={600}>{category.title}</Text>
                  <Badge size="sm">
                    {todos.filter(category.filter).length}
                  </Badge>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <AccordionSection category={category} todos={todos} />
              </Accordion.Panel>
            </Accordion.Item>
          ))}
        </Accordion>
      </Stack>
    </Paper>
  );
};

export default TodoList; 